import { useCallback } from 'react';
import type { DragEvent } from 'react';

import asset_file_names from '@/config/asset-file-names.json';
import {
  type AssetResourceSourceUrls,
  get_asset_resource_source_urls,
} from '@/lib/asset-resource-url';

export type AssetResourceKind = 'image' | 'video';

type BaseAssetResource = {
  asset_sequence_number: number;
  fallback_source_url: string | null;
  file_name: string;
  label: string;
  source_url: string;
};

export type ImageAssetResource = BaseAssetResource & {
  kind: 'image';
  preview_fallback_source_url: string | null;
  preview_source_url: string;
};

export type VideoAssetResource = BaseAssetResource & {
  kind: 'video';
  poster_fallback_source_url: string | null;
  poster_source_url: string;
  responsive_variant_source_urls?: ResponsiveVideoAssetSourceUrls;
};

export type AssetResource = ImageAssetResource | VideoAssetResource;

export type VideoAssetVariantSourceUrls = AssetResourceSourceUrls & {
  poster_fallback_source_url: string | null;
  poster_source_url: string;
};

export type ResponsiveVideoAssetSourceUrls = {
  desktop: VideoAssetVariantSourceUrls;
  mobile: VideoAssetVariantSourceUrls;
};

type UseAppAssetsResult = {
  asset_resources: AssetResource[];
  handle_blocker_drag_event: (drag_event: DragEvent<HTMLDivElement>) => void;
  handle_refresh_menu_select: () => void;
};

const VIDEO_FILE_NAME_PATTERN = /_video\.[^.]+$/;
const IMAGE_FILE_NAME_PATTERN = /_image\.[^.]+$/;
const RESPONSIVE_VIDEO_ASSET_FILE_NAMES = new Set(['002_video.mp4']);

const asset_resources: AssetResource[] =
  asset_file_names.map(create_asset_resource);

export function use_app_assets(): UseAppAssetsResult {
  const handle_blocker_drag_event = useCallback(
    (drag_event: DragEvent<HTMLDivElement>) => {
      drag_event.preventDefault();
      drag_event.stopPropagation();
    },
    [],
  );

  const handle_refresh_menu_select = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    asset_resources,
    handle_blocker_drag_event,
    handle_refresh_menu_select,
  };
}

export function create_asset_resource(file_name: string): AssetResource {
  const asset_resource_kind = get_asset_resource_kind(file_name);
  const asset_sequence_number = get_asset_sequence_number(file_name);

  if (asset_resource_kind === 'video') {
    return create_video_asset_resource({
      asset_sequence_number,
      file_name,
      label: get_asset_resource_label(file_name),
    });
  }

  const source_urls = get_asset_resource_source_urls(file_name);
  const base_asset_resource = {
    asset_sequence_number,
    fallback_source_url: source_urls.fallback_source_url,
    file_name,
    label: get_asset_resource_label(file_name),
    source_url: source_urls.source_url,
  };

  const preview_source_urls = get_image_asset_preview_source_urls(file_name);

  return {
    ...base_asset_resource,
    kind: asset_resource_kind,
    preview_fallback_source_url: preview_source_urls.fallback_source_url,
    preview_source_url: preview_source_urls.source_url,
  };
}

function create_video_asset_resource({
  asset_sequence_number,
  file_name,
  label,
}: {
  asset_sequence_number: number;
  file_name: string;
  label: string;
}): VideoAssetResource {
  const responsive_variant_source_urls =
    get_responsive_video_asset_source_urls(file_name);
  const source_urls = responsive_variant_source_urls?.desktop ?? {
    ...get_asset_resource_source_urls(file_name),
    ...get_video_asset_poster_source_urls(file_name),
  };

  return {
    asset_sequence_number,
    fallback_source_url: source_urls.fallback_source_url,
    file_name,
    kind: 'video',
    label,
    poster_fallback_source_url: source_urls.poster_fallback_source_url,
    poster_source_url: source_urls.poster_source_url,
    responsive_variant_source_urls,
    source_url: source_urls.source_url,
  };
}

function get_asset_resource_kind(file_name: string): AssetResourceKind {
  return file_name.includes('_video.') ? 'video' : 'image';
}

function get_asset_sequence_number(file_name: string) {
  const [asset_sequence_number] = file_name.split('_');
  const parsed_asset_sequence_number = Number(asset_sequence_number);

  if (!Number.isInteger(parsed_asset_sequence_number)) {
    throw new Error(
      `get_asset_sequence_number failed: invalid file_name=${file_name}`,
    );
  }

  return parsed_asset_sequence_number;
}

function get_asset_resource_label(file_name: string) {
  const [asset_sequence_number] = file_name.split('_');

  return `资源 ${asset_sequence_number}`;
}

function get_video_asset_poster_source_urls(file_name: string) {
  const poster_file_name = file_name.replace(
    VIDEO_FILE_NAME_PATTERN,
    '_poster.jpg',
  );
  const poster_source_urls = get_asset_resource_source_urls(poster_file_name);

  return {
    poster_fallback_source_url: poster_source_urls.fallback_source_url,
    poster_source_url: poster_source_urls.source_url,
  };
}

function get_responsive_video_asset_source_urls(
  file_name: string,
): ResponsiveVideoAssetSourceUrls | undefined {
  if (!RESPONSIVE_VIDEO_ASSET_FILE_NAMES.has(file_name)) {
    return undefined;
  }

  return {
    desktop: get_video_asset_variant_source_urls({
      file_name,
      variant_name: 'desktop',
    }),
    mobile: get_video_asset_variant_source_urls({
      file_name,
      variant_name: 'mobile',
    }),
  };
}

function get_video_asset_variant_source_urls({
  file_name,
  variant_name,
}: {
  file_name: string;
  variant_name: 'desktop' | 'mobile';
}): VideoAssetVariantSourceUrls {
  const source_file_name = file_name.replace(
    VIDEO_FILE_NAME_PATTERN,
    `_video.${variant_name}.mp4`,
  );
  const poster_file_name = file_name.replace(
    VIDEO_FILE_NAME_PATTERN,
    `_poster.${variant_name}.jpg`,
  );
  const source_urls = get_asset_resource_source_urls(source_file_name);
  const poster_source_urls = get_asset_resource_source_urls(poster_file_name);

  return {
    fallback_source_url: source_urls.fallback_source_url,
    poster_fallback_source_url: poster_source_urls.fallback_source_url,
    poster_source_url: poster_source_urls.source_url,
    source_url: source_urls.source_url,
  };
}

function get_image_asset_preview_source_urls(file_name: string) {
  const preview_file_name = file_name.replace(
    IMAGE_FILE_NAME_PATTERN,
    '_preview.webp',
  );

  return get_asset_resource_source_urls(preview_file_name);
}
